import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RefreshCcw } from 'lucide-react';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { TILES } from '../landing/indexTiles';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const src = (url) => (url.startsWith('http') ? url : `${BACKEND}${url}`);

function PhotoCard({ item, onSave }) {
  const [pillar, setPillar] = useState(item.homepage_pillar || '');
  const [missing, setMissing] = useState(false);
  const live = item.provider_status === 'approved';
  return (
    <li className="card-surface overflow-hidden" data-testid={`homepage-photo-${item.image_id}`}>
      <div className="aspect-[4/5] bg-stone-100">
        {missing ? (
          <div className="w-full h-full flex items-center justify-center text-xs text-stone-400 p-4 text-center" data-testid={`homepage-photo-missing-${item.image_id}`}>
            Image file missing - the homepage will use stock instead
          </div>
        ) : (
          <img src={src(item.url)} alt={item.caption || 'Portfolio photo'} loading="lazy" onError={() => setMissing(true)} className="w-full h-full object-cover" />
        )}
      </div>
      <div className="p-3 space-y-2 text-sm">
        <p className="font-medium text-[#1A1A1A] truncate">{item.provider_name}</p>
        {!live && <p className="text-xs text-amber-700">Provider is {item.provider_status || 'not live'} - photo stays hidden</p>}
        {item.caption && <p className="text-xs text-stone-500 line-clamp-2">{item.caption}</p>}
        <Select value={pillar} onValueChange={(v) => { setPillar(v); if (item.homepage_approved) onSave(item, true, v); }}>
          <SelectTrigger className="h-8 text-xs" data-testid={`homepage-photo-pillar-${item.image_id}`}>
            <SelectValue placeholder="Choose a tile" />
          </SelectTrigger>
          <SelectContent>
            {TILES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center justify-between gap-2 text-xs text-stone-600">
          Show on homepage
          <Switch checked={item.homepage_approved} onCheckedChange={(on) => onSave(item, on, pillar)}
            data-testid={`homepage-photo-toggle-${item.image_id}`} />
        </label>
      </div>
    </li>
  );
}

export default function AdminHomepagePhotosTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/homepage-photos');
      setItems(res.data.items);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load photos');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (item, approved, pillar) => {
    try {
      await api.put(`/admin/homepage-photos/${item.image_id}`, { approved, pillar: pillar || null });
      setItems((prev) => prev.map((i) => (i.image_id === item.image_id ? { ...i, homepage_approved: approved, homepage_pillar: pillar } : i)));
      toast.success(approved ? 'Photo is on the homepage' : 'Photo removed from the homepage');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    }
  };

  const approvedCount = items.filter((i) => i.homepage_approved).length;
  return (
    <div className="space-y-4" data-testid="admin-homepage-photos">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading text-xl text-[#1A1A1A]">Homepage photos</h3>
          <p className="text-sm text-stone-500">
            Pick provider portfolio photos for The Index tiles. Up to 3 per tile; stock photos fill any gaps. {approvedCount} live.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="homepage-photos-refresh">
          <RefreshCcw className="h-4 w-4 mr-1.5" />Refresh
        </Button>
      </div>
      {loading ? (
        <p className="text-sm text-stone-400">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-stone-500" data-testid="homepage-photos-empty">No portfolio photos uploaded yet.</p>
      ) : (
        <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {items.map((item) => <PhotoCard key={item.image_id} item={item} onSave={save} />)}
        </ul>
      )}
    </div>
  );
}
